/**
 * Hook de Contas a Receber (Fase 1, Zeus-only — endpoint /api/contas-receber,
 * PR #312). Recebível de energia a vencer pros sócios.
 *
 * Mesmo padrão dos demais hooks de dados: cache module-level + refetch
 * periódico com guard de visibilidade (#75 — não pola aba em background).
 * Falha (rede/auth/5xx) → data null + error; a página decide o estado honesto.
 */
import { useEffect, useState } from "react";
import { getContasReceber } from "../api/client";
import type { ContasReceberResponse } from "../api/types";
import { startVisiblePolling } from "./visiblePolling";

const REFETCH_MS = 180 * 1000; // fonte Zeus muda via cron diário — 3 min basta

interface CacheEntry {
  expiresAt: number;
  promise: Promise<ContasReceberResponse>;
}

let cache: CacheEntry | null = null;

export function invalidateContasReceberCache(): void {
  cache = null;
}

export interface UseContasReceberState {
  data: ContasReceberResponse | null;
  isLoading: boolean;
  error: Error | null;
}

export function useContasReceber(): UseContasReceberState {
  const [state, setState] = useState<UseContasReceberState>({
    data: null,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    let cancel = false;

    function load() {
      const now = Date.now();
      if (!cache || cache.expiresAt < now) {
        cache = { expiresAt: now + REFETCH_MS, promise: getContasReceber() };
      }
      cache.promise
        .then((data) => {
          if (cancel) return;
          setState({ data, isLoading: false, error: null });
        })
        .catch((err: Error) => {
          if (cancel) return;
          cache = null;
          setState({ data: null, isLoading: false, error: err });
        });
    }

    load();
    const stop = startVisiblePolling(load, REFETCH_MS);
    return () => {
      cancel = true;
      stop();
    };
  }, []);

  return state;
}
